/**
 * Perfora GA4 Ecommerce dataLayer
 * Portable module — pair with snippets/ga4-datalayer.liquid + snippets/ga4-item.liquid
 *
 * Markup hooks:
 *   [data-ga4-list][data-ga4-list-id][data-ga4-list-name]
 *   [data-ga4-item]  — JSON item payload on product card
 *   [data-ga4-promo][data-ga4-promo-id][data-ga4-promo-name]
 *     [data-ga4-creative-slot][data-ga4-creative-name]
 */
(function () {
  'use strict';

  var CFG = window.PerforaGA4 || {};
  var STORAGE_KEY = 'perfora_ga4_list_attr';
  var viewedPromos = new WeakSet();
  var viewedItems = new Set();
  var selectBound = new WeakSet();

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function platform() {
    return window.matchMedia('(max-width: 768px)').matches ? 'm-web' : 'd-web';
  }

  function commonParams() {
    var params = {
      page_type: CFG.page_type || 'other_page',
      platform: platform(),
      user_status: CFG.user_status || 'guest'
    };
    if (CFG.user_status === 'logged_in' && CFG.user_id_event != null) {
      params.user_id_event = String(CFG.user_id_event);
    } else {
      params.user_id_event = undefined;
    }
    return params;
  }

  function pushEvent(eventName, ecommerce, extra) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    var payload = Object.assign({ event: eventName }, commonParams(), extra || {}, {
      ecommerce: ecommerce
    });
    window.dataLayer.push(payload);
    if (window.PerforaGA4 && window.PerforaGA4.debug) {
      console.log('[GA4]', eventName, payload);
    }
  }

  function money(n) {
    var v = Number(n);
    return isNaN(v) ? 0 : Math.round(v * 100) / 100;
  }

  function parseItem(el) {
    if (!el) return null;
    var raw = el.getAttribute('data-ga4-item');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function getListContext(el) {
    var list = el.closest('[data-ga4-list]');
    if (!list) {
      return {
        item_list_id: 'NA',
        item_list_name: 'NA',
        filter_applied: CFG.filter_applied || 'NA',
        sort_applied: CFG.sort_applied || 'NA'
      };
    }
    return {
      item_list_id: list.getAttribute('data-ga4-list-id') || 'NA',
      item_list_name: list.getAttribute('data-ga4-list-name') || 'NA',
      filter_applied: list.getAttribute('data-ga4-filter') || CFG.filter_applied || 'NA',
      sort_applied: list.getAttribute('data-ga4-sort') || CFG.sort_applied || 'NA'
    };
  }

  function indexInList(el, list) {
    if (!list) return 0;
    var items = Array.prototype.slice.call(list.querySelectorAll('[data-ga4-item]'));
    var idx = items.indexOf(el);
    return idx < 0 ? 0 : idx;
  }

  function enrichItem(item, el) {
    if (!item) return null;
    var ctx = getListContext(el);
    var list = el.closest('[data-ga4-list]');
    var stored = readListAttr(item.item_id);
    return Object.assign({}, item, {
      item_list_id: item.item_list_id && item.item_list_id !== 'NA' ? item.item_list_id : (stored.item_list_id || ctx.item_list_id),
      item_list_name: item.item_list_name && item.item_list_name !== 'NA' ? item.item_list_name : (stored.item_list_name || ctx.item_list_name),
      index: typeof item.index === 'number' ? item.index : indexInList(el, list),
      filter_applied: ctx.filter_applied,
      sort_applied: ctx.sort_applied,
      coupon: item.coupon || 'NA',
      discount: money(item.discount || 0),
      price: money(item.price || 0),
      quantity: item.quantity || 1,
      affiliation: item.affiliation || CFG.shop_name || 'NA'
    });
  }

  function stripInternal(item, includeFilters) {
    var copy = Object.assign({}, item);
    delete copy.product_id;
    delete copy.variant_id;
    if (!includeFilters) {
      delete copy.filter_applied;
      delete copy.sort_applied;
    }
    return copy;
  }

  function saveListAttr(item) {
    try {
      var map = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      map[String(item.item_id)] = {
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
        index: item.index
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch (e) {}
  }

  function readListAttr(itemId) {
    try {
      var map = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      return map[String(itemId)] || {};
    } catch (e) {
      return {};
    }
  }

  function itemsValue(items) {
    return money(
      (items || []).reduce(function (sum, it) {
        return sum + money(it.price) * (it.quantity || 1);
      }, 0)
    );
  }

  function promoPayload(el) {
    return {
      promotion_id: el.getAttribute('data-ga4-promo-id') || 'NA',
      promotion_name: el.getAttribute('data-ga4-promo-name') || 'NA',
      creative_slot: el.getAttribute('data-ga4-creative-slot') || 'NA',
      creative_name: el.getAttribute('data-ga4-creative-name') || 'NA'
    };
  }

  function trackViewPromotion(el) {
    if (!el || viewedPromos.has(el)) return;
    viewedPromos.add(el);
    pushEvent('view_promotion', promoPayload(el));
  }

  function trackSelectPromotion(el) {
    if (!el) return;
    pushEvent('select_promotion', promoPayload(el));
  }

  function bindPromoClicks(root) {
    (root || document).querySelectorAll('[data-ga4-promo]').forEach(function (el) {
      if (selectBound.has(el)) return;
      selectBound.add(el);
      el.addEventListener('click', function () {
        trackSelectPromotion(el);
      }, true);
    });
  }

  function trackViewItemList(listEl, visibleEls) {
    if (!listEl || !visibleEls.length) return;
    var listId = listEl.getAttribute('data-ga4-list-id') || 'NA';
    var listName = listEl.getAttribute('data-ga4-list-name') || 'NA';
    var filter = listEl.getAttribute('data-ga4-filter') || CFG.filter_applied || 'NA';
    var sort = listEl.getAttribute('data-ga4-sort') || CFG.sort_applied || 'NA';
    var keyPrefix = listId + '::';
    var items = [];

    visibleEls.forEach(function (el) {
      var itemKey = keyPrefix + (el.getAttribute('data-ga4-item-id') || el.getAttribute('data-ga4-item') || '');
      if (viewedItems.has(itemKey)) return;
      var item = enrichItem(parseItem(el), el);
      if (!item) return;
      viewedItems.add(itemKey);
      item.item_list_id = listId;
      item.item_list_name = listName;
      item.filter_applied = filter;
      item.sort_applied = sort;
      saveListAttr(item);
      items.push(stripInternal(item, true));
    });

    if (!items.length) return;
    pushEvent('view_item_list', {
      currency: CFG.currency || 'INR',
      item_list_id: listId,
      item_list_name: listName,
      items: items
    });
  }

  function trackSelectItem(el) {
    var item = enrichItem(parseItem(el), el);
    if (!item) return;
    saveListAttr(item);
    pushEvent('select_item', {
      currency: CFG.currency || 'INR',
      item_list_id: item.item_list_id,
      item_list_name: item.item_list_name,
      items: [stripInternal(item, false)]
    });
  }

  function bindItemClicks(root) {
    (root || document).querySelectorAll('[data-ga4-item]').forEach(function (el) {
      if (selectBound.has(el)) return;
      selectBound.add(el);
      el.addEventListener('click', function (e) {
        var link = e.target.closest('a[href]');
        var quickBuy = e.target.closest('[onclick*="dialogQuickShop"], .quickbuy__btn, .product__quickbuy button');
        if (!link || quickBuy) return;
        trackSelectItem(el);
      }, true);
    });
  }

  function trackViewItem() {
    if (CFG.page_type !== 'product_page' || !CFG.product) return;
    var item = Object.assign({}, CFG.product);
    var stored = readListAttr(item.item_id);
    if (stored.item_list_id) {
      item.item_list_id = stored.item_list_id;
      item.item_list_name = stored.item_list_name;
      item.index = stored.index != null ? stored.index : item.index;
    }
    pushEvent('view_item', {
      currency: CFG.currency || 'INR',
      value: money(item.price * (item.quantity || 1)),
      items: [stripInternal(item, false)]
    });
  }

  function cartItemsFromConfig() {
    return (CFG.cart && CFG.cart.items ? CFG.cart.items : []).map(function (it) {
      var stored = readListAttr(it.item_id);
      return stripInternal(
        Object.assign({}, it, {
          item_list_id: stored.item_list_id || it.item_list_id || 'NA',
          item_list_name: stored.item_list_name || it.item_list_name || 'NA',
          index: stored.index != null ? stored.index : it.index
        }),
        false
      );
    });
  }

  function trackViewCart() {
    if (CFG.page_type !== 'cart_page') return;
    var items = cartItemsFromConfig();
    pushEvent('view_cart', {
      currency: CFG.currency || 'INR',
      value: itemsValue(items),
      items: items
    });
  }

  function trackBeginCheckout() {
    var items = cartItemsFromConfig();
    if (!items.length) return;
    pushEvent('begin_checkout', {
      currency: CFG.currency || 'INR',
      value: itemsValue(items),
      coupon: 'NA',
      items: items
    });
  }

  function bindCheckoutClicks() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[name="checkout"], [href*="/checkout"], button[type="submit"][name="checkout"], .shopify-payment-button__button');
      if (!btn) return;
      trackBeginCheckout();
    }, true);
  }

  function findCardItemByVariant(variantId) {
    var el = document.querySelector('[data-ga4-item*="\\"variant_id\\":' + variantId + '"], [data-ga4-item*="\\"variant_id\\": ' + variantId + '"]');
    if (el) return enrichItem(parseItem(el), el);
    if (CFG.product && String(CFG.product.variant_id) === String(variantId)) {
      return Object.assign({}, CFG.product);
    }
    return null;
  }

  function itemFromCartLine(line) {
    if (!line) return null;
    var variant = line.variant_id || line.id;
    var fromDom = findCardItemByVariant(variant);
    var stored = readListAttr(fromDom && fromDom.item_id ? fromDom.item_id : line.sku || line.id);
    var price = line.final_price != null ? line.final_price / 100 : line.price != null ? line.price / 100 : 0;
    var compare = line.original_price != null ? line.original_price / 100 : price;
    var discount = Math.max(0, money(compare - price));
    return {
      item_id: (fromDom && fromDom.item_id) || line.sku || String(line.variant_id || line.id),
      item_name: (fromDom && fromDom.item_name) || line.product_title || line.title || 'NA',
      affiliation: CFG.shop_name || 'NA',
      coupon: 'NA',
      discount: discount,
      item_brand: (fromDom && fromDom.item_brand) || line.vendor || CFG.shop_name || 'NA',
      item_category: (fromDom && fromDom.item_category) || 'NA',
      item_category2: (fromDom && fromDom.item_category2) || 'NA',
      item_category3: (fromDom && fromDom.item_category3) || 'NA',
      item_category4: (fromDom && fromDom.item_category4) || 'NA',
      item_category5: (fromDom && fromDom.item_category5) || 'NA',
      item_list_id: (fromDom && fromDom.item_list_id) || stored.item_list_id || 'NA',
      item_list_name: (fromDom && fromDom.item_list_name) || stored.item_list_name || 'NA',
      item_variant: (fromDom && fromDom.item_variant) || line.variant_title || 'Default Title',
      price: money(price),
      quantity: line.quantity || 1,
      index: (fromDom && fromDom.index) || stored.index || 0
    };
  }

  function trackAddToCart(items) {
    if (!items || !items.length) return;
    var clean = items.map(function (it) {
      saveListAttr(it);
      return stripInternal(it, false);
    });
    pushEvent('add_to_cart', {
      currency: CFG.currency || 'INR',
      value: itemsValue(clean),
      items: clean
    });
  }

  function trackRemoveFromCart(items) {
    if (!items || !items.length) return;
    var clean = items.map(function (it) { return stripInternal(it, false); });
    pushEvent('remove_from_cart', {
      currency: CFG.currency || 'INR',
      value: itemsValue(clean),
      items: clean
    });
  }

  function enhanceCartLineMarkup() {
    if (!CFG.cart || !CFG.cart.items) return;
    document.querySelectorAll('.cart-item, [id^="cartItem-"]').forEach(function (row, i) {
      if (row.getAttribute('data-ga4-item')) return;
      var item = CFG.cart.items[i];
      if (!item) return;
      row.setAttribute('data-ga4-item', JSON.stringify(item));
    });
  }

  function interceptCartFetch() {
    if (window.__perforaGA4FetchPatched) return;
    window.__perforaGA4FetchPatched = true;
    var nativeFetch = window.fetch;
    var previousCartItems = null;

    function snapshotCart(done) {
      fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', {
        credentials: 'same-origin'
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          previousCartItems = (cart.items || []).map(function (line) {
            return { key: line.key, variant_id: line.variant_id, quantity: line.quantity, line: line };
          });
          CFG.cart = CFG.cart || {};
          CFG.cart.item_count = cart.item_count;
          CFG.cart.total_price = (cart.total_price || 0) / 100;
          CFG.cart._raw = cart;
          if (typeof done === 'function') done(cart);
        })
        .catch(function () {});
    }

    snapshotCart();

    window.fetch = function () {
      var args = arguments;
      var input = args[0];
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var method = (args[1] && args[1].method) || (input && input.method) || 'GET';
      method = String(method).toUpperCase();
      var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url) && method !== 'GET';
      var isChange = /\/cart\/change(\.js)?(\?|$)/.test(url) && method !== 'GET';
      var isUpdate = /\/cart\/update(\.js)?(\?|$)/.test(url) && method !== 'GET';
      var before = previousCartItems ? previousCartItems.slice() : null;

      return nativeFetch.apply(this, args).then(function (response) {
        var cloned = response.clone();
        if (isAdd || isChange || isUpdate) {
          cloned.json().then(function (data) {
            if (isAdd) {
              var lines = Array.isArray(data.items) ? data.items : data.id ? [data] : [];
              var mapped = lines.map(itemFromCartLine).filter(Boolean);
              if (mapped.length) trackAddToCart(mapped);
              snapshotCart();
              return;
            }
            snapshotCart(function (cart) {
              if (!before) return;
              var afterMap = {};
              (cart.items || []).forEach(function (line) { afterMap[line.key] = line; });
              var removed = [];
              before.forEach(function (prev) {
                var next = afterMap[prev.key];
                if (!next) {
                  var item = itemFromCartLine(prev.line);
                  if (item) { item.quantity = prev.quantity; removed.push(item); }
                } else if (next.quantity < prev.quantity) {
                  var item2 = itemFromCartLine(next);
                  if (item2) { item2.quantity = prev.quantity - next.quantity; removed.push(item2); }
                }
              });
              if (removed.length) trackRemoveFromCart(removed);
            });
          }).catch(function () {});
        }
        return response;
      });
    };
  }

  function observeViewport() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-ga4-list]').forEach(function (list) {
        trackViewItemList(list, Array.prototype.slice.call(list.querySelectorAll('[data-ga4-item]')));
      });
      document.querySelectorAll('[data-ga4-promo]').forEach(trackViewPromotion);
      return;
    }

    var listObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        if (el.hasAttribute('data-ga4-promo')) {
          trackViewPromotion(el);
          listObserver.unobserve(el);
          return;
        }
        if (el.hasAttribute('data-ga4-item')) {
          var list = el.closest('[data-ga4-list]');
          if (!list) return;
          var visibles = Array.prototype.slice
            .call(list.querySelectorAll('[data-ga4-item]'))
            .filter(function (itemEl) {
              var rect = itemEl.getBoundingClientRect();
              return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
            });
          trackViewItemList(list, visibles);
        }
      });
    }, { threshold: 0.5 });

    function observeAll(root) {
      (root || document).querySelectorAll('[data-ga4-promo]').forEach(function (el) {
        listObserver.observe(el);
      });
      (root || document).querySelectorAll('[data-ga4-list] [data-ga4-item]').forEach(function (el) {
        listObserver.observe(el);
      });
      bindPromoClicks(root);
      bindItemClicks(root);
    }

    observeAll(document);

    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          observeAll(node);
        });
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.PerforaGA4 = Object.assign(CFG, {
    pushEvent: pushEvent,
    trackAddToCart: trackAddToCart,
    trackRemoveFromCart: trackRemoveFromCart,
    trackBeginCheckout: trackBeginCheckout,
    debug: false
  });

  ready(function () {
    interceptCartFetch();
    enhanceCartLineMarkup();
    observeViewport();
    trackViewItem();
    trackViewCart();
    bindCheckoutClicks();
  });
})();